import React, { useState, useEffect } from "react";
import {
    getSubscriptions,
    createSubscription,
    updateSubscription,
    deleteSubscription,
    reorderSubscriptions,
    clearCache,
} from "../../services/api";
import SubscriptionForm from "./SubscriptionForm";
import PerksManagement from "./PerksManagement";
import { Edit2, Plus, Trash2, ArrowLeft, ArrowRight } from "lucide-react";

const SubscriptionManagement = () => {
    const [subscriptions, setSubscriptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [editingData, setEditingData] = useState(null);
    const [hasOrderChanges, setHasOrderChanges] = useState(false);
    const [savingOrder, setSavingOrder] = useState(false);

    useEffect(() => {
        fetchSubscriptions();
    }, []);

    const fetchSubscriptions = async () => {
        try {
            setLoading(true);
            const res = await getSubscriptions();
            if (res.data && res.data.success) {
                setSubscriptions(res.data.data);
            }
        } catch (err) {
            console.error("Error fetching subscriptions:", err);
            setError("Failed to fetch subscriptions");
        } finally {
            setLoading(false);
        }
    };

    const handleAddNew = () => {
        setEditingData(null);
        setShowForm(true);
    };

    const handleEdit = (subscription) => {
        setEditingData(subscription);
        setShowForm(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this subscription?")) return;

        try {
            const res = await deleteSubscription(id);
            if (res.data && res.data.success) {
                clearCache("subscriptions");
                fetchSubscriptions();
            }
        } catch (err) {
            console.error("Error deleting subscription:", err);
            alert("Failed to delete subscription");
        }
    };

    const handleMove = async (index, direction) => {
        const newSubscriptions = [...subscriptions];
        const targetIndex = direction === "left" ? index - 1 : index + 1;

        if (targetIndex < 0 || targetIndex >= newSubscriptions.length) return;

        // Swap locally
        [newSubscriptions[index], newSubscriptions[targetIndex]] = [
            newSubscriptions[targetIndex],
            newSubscriptions[index],
        ];

        setSubscriptions(newSubscriptions);
        setHasOrderChanges(true);
    };

    const handleSaveOrder = async () => {
        setSavingOrder(true);
        try {
            const orderedIds = subscriptions.map(s => s._id);
            const res = await reorderSubscriptions(orderedIds);
            if (res.data && res.data.success) {
                clearCache("subscriptions");
                setHasOrderChanges(false);
                alert("Subscription order saved successfully!");
            }
        } catch (err) {
            console.error("Error saving subscription order:", err);
            alert("Failed to save order");
        } finally {
            setSavingOrder(false);
        }
    };

    const handleFormSubmit = async (formData) => {
        try {
            if (formData._id) {
                // Update
                const res = await updateSubscription(formData);
                if (res.data && res.data.success) {
                    clearCache("subscriptions");
                    await fetchSubscriptions();
                    setShowForm(false);
                }
            } else {
                // Create
                const res = await createSubscription(formData);
                if (res.data && res.data.success) {
                    clearCache("subscriptions");
                    await fetchSubscriptions();
                    setShowForm(false);
                }
            }
        } catch (err) {
            console.error("Error saving subscription:", err);
            alert("Failed to save subscription");
        }
    };

    if (loading && subscriptions.length === 0) {
        return <div className="p-8 text-center text-gray-500">Loading subscriptions...</div>;
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center bg-white p-4 rounded-lg shadow-sm">
                <h2 className="text-xl font-semibold text-gray-800">
                    Subscription Management
                </h2>
                <div className="flex items-center space-x-3">
                    {hasOrderChanges && (
                        <button
                            onClick={handleSaveOrder}
                            disabled={savingOrder}
                            className={`bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition flex items-center ${savingOrder ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                        >
                            {savingOrder ? "Saving..." : "Save Order"}
                        </button>
                    )}
                    {!showForm && (
                        <button
                            onClick={handleAddNew}
                            className="flex items-center space-x-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition"
                        >
                            <Plus className="w-5 h-5" />
                            <span>Add Subscription</span>
                        </button>
                    )}
                </div>
            </div>

            {error && (
                <div className="bg-red-50 text-red-600 p-4 rounded-lg">
                    {error}
                </div>
            )}

            {showForm && (
                <SubscriptionForm
                    initialData={editingData}
                    onSubmit={handleFormSubmit}
                    onCancel={() => setShowForm(false)}
                />
            )}

            {/* Subscription List */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden auto-rows-max">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
                    {subscriptions.length === 0 && !showForm ? (
                        <div className="col-span-full text-center py-12 text-gray-500">
                            No subscriptions found. Click "Add Subscription" to create one.
                        </div>
                    ) : (
                        subscriptions.map((sub) => (
                            <div
                                key={sub._id}
                                className={`border rounded-xl p-6 relative ${sub.recommended ? 'border-green-500 shadow-md ring-1 ring-green-500' : 'border-gray-200'
                                    }`}
                            >
                                {sub.recommended && (
                                    <span className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                                        Recommended
                                    </span>
                                )}

                                <div className="absolute top-4 right-4 flex space-x-2">
                                    <button
                                        onClick={() => handleEdit(sub)}
                                        className="p-2 text-gray-400 hover:text-blue-500 bg-gray-50 rounded-full hover:bg-blue-50 transition"
                                    >
                                        <Edit2 className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => handleDelete(sub._id)}
                                        className="p-2 text-gray-400 hover:text-red-500 bg-gray-50 rounded-full hover:bg-red-50 transition"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>

                                {/* Reordering Controls */}
                                <div className="absolute top-4 left-4 flex space-x-2">
                                    <button
                                        onClick={() => handleMove(subscriptions.indexOf(sub), "left")}
                                        disabled={subscriptions.indexOf(sub) === 0}
                                        className={`p-2 bg-white rounded-full shadow-sm border transition ${subscriptions.indexOf(sub) === 0 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-gray-50'
                                            }`}
                                        title="Move Left"
                                    >
                                        <ArrowLeft className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => handleMove(subscriptions.indexOf(sub), "right")}
                                        disabled={subscriptions.indexOf(sub) === subscriptions.length - 1}
                                        className={`p-2 bg-white rounded-full shadow-sm border transition ${subscriptions.indexOf(sub) === subscriptions.length - 1 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-gray-50'
                                            }`}
                                        title="Move Right"
                                    >
                                        <ArrowRight className="w-4 h-4" />
                                    </button>
                                </div>

                                <div className="mb-4 pr-16">
                                    <h3 className="text-xl font-bold text-gray-900">{sub.name}</h3>
                                    {sub.subtext && <p className="text-sm text-gray-500">{sub.subtext}</p>}
                                </div>

                                <div className="mb-6 flex items-baseline">
                                    {sub.prefix && <span className="text-gray-500 text-sm mr-1">{sub.prefix}</span>}

                                    {sub.discountPrice ? (
                                        <div className="flex flex-col">
                                            <span className="text-3xl font-extrabold text-gray-900">₹{sub.discountPrice}</span>
                                            <span className="text-sm text-gray-500 line-through">₹{sub.actualPrice}</span>
                                        </div>
                                    ) : (
                                        <span className="text-3xl font-extrabold text-gray-900">₹{sub.actualPrice}</span>
                                    )}

                                    {sub.period && <span className="text-gray-500 ml-1">{sub.period}</span>}
                                </div>

                                {sub.limit && (
                                    <div className="bg-gray-50 p-2 rounded-lg text-center text-sm font-medium text-gray-700 mb-4 border border-gray-100">
                                        {sub.limit}
                                    </div>
                                )}

                                {sub.features && sub.features.length > 0 && (
                                    <ul className="space-y-3 mt-4 border-t pt-4">
                                        {sub.features.map((feature, index) => (
                                            <li key={index} className="flex items-start">
                                                <svg className="w-5 h-5 text-green-500 shrink-0 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                </svg>
                                                <span className="text-gray-600 text-sm">{feature}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </div>

            <div className="mt-12 pt-8 border-t-2 border-gray-100">
                <PerksManagement />
            </div>
        </div>
    );
};

export default SubscriptionManagement;
